import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, LogOut, UserRound } from 'lucide-react';
import { useId, useRef } from 'react';
import { Link } from 'react-router';

import { errorMessage } from '../../lib/api.ts';
import type { Session } from '../../lib/session.ts';
import { chooseTheme } from '../../lib/theme.ts';
import { useToast } from '../../lib/toast.tsx';
import { ThemePicker } from '../ui/theme-picker.tsx';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

/**
 * The user's menu (name, role, Mi cuenta, theme, Cerrar sesión) on the native
 * `popover` attribute: the browser handles opening, Esc and clicking outside, with no
 * injected styles (strict CSP).
 */
export function UserMenu({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const client = useQueryClient();
  const notify = useToast();
  const close = () => panel.current?.hidePopover();
  const role = session.membership.role?.name ?? 'Sin rol';

  return (
    <>
      <button
        type="button"
        popoverTarget={id}
        aria-label={`Menú de ${session.user.fullName}`}
        className="flex min-h-11 items-center gap-2 rounded-lg px-2 hover:bg-muted"
      >
        <span
          aria-hidden="true"
          className="grid size-8 place-items-center rounded-full bg-soft text-xs font-semibold text-soft-foreground"
        >
          {initials(session.user.fullName)}
        </span>
        <span className="hidden text-left text-sm sm:block">
          <span className="block font-medium">{session.user.fullName}</span>
          <span className="block text-xs text-muted-foreground">{role}</span>
        </span>
        <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
      </button>
      <div
        ref={panel}
        id={id}
        popover="auto"
        className="fixed inset-auto top-14 right-3 m-0 w-72 rounded-xl border border-border bg-surface p-2 text-foreground shadow-lg"
      >
        <div className="border-b border-border px-3 pt-2 pb-3">
          <p className="font-medium">{session.user.fullName}</p>
          <p className="text-sm text-muted-foreground">
            {role} · {session.tenant.name}
          </p>
        </div>
        <Link
          to="/mi-cuenta"
          onClick={close}
          className="mt-2 flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm hover:bg-muted"
        >
          <UserRound className="size-4" aria-hidden="true" /> Mi cuenta
        </Link>
        <ThemePicker
          className="px-3 py-2"
          onChange={(value) => {
            chooseTheme(client, session, value).catch((error: unknown) => {
              notify(`No se pudo guardar el tema: ${errorMessage(error)}`, 'error');
            });
          }}
        >
          {session.impersonation && (
            <p className="pt-2 text-xs text-muted-foreground">
              Durante la suplantación el tema solo cambia en esta pantalla.
            </p>
          )}
        </ThemePicker>
        <button
          type="button"
          onClick={() => {
            close();
            onLogout();
          }}
          className="flex min-h-11 w-full items-center gap-3 rounded-lg border-t border-border px-3 text-sm hover:bg-muted"
        >
          <LogOut className="size-4" aria-hidden="true" /> Cerrar sesión
        </button>
      </div>
    </>
  );
}
